import { useState, useEffect, useCallback } from 'react';
import { collection, query, doc, updateDoc, onSnapshot, where, addDoc, orderBy } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

export function useAdmin() {
  const { currentUser, userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingGuides, setPendingGuides] = useState([]);
  const [logs, setLogs] = useState([]);
  const [userLogs, setUserLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Ensure user is admin
  const isAdmin = userProfile?.isAdmin === true;

  const logAdminAction = useCallback(async (action, details) => {
    if (!isAdmin || !currentUser) return;
    try {
      await addDoc(collection(db, 'admin_logs'), {
        adminId: currentUser.uid,
        adminName: userProfile?.displayName || 'Admin',
        action,
        details,
        timestamp: new Date().toISOString()
      });
    } catch (e) {
      console.error("Failed to log admin action", e);
    }
  }, [isAdmin, currentUser, userProfile]);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    // Subscribe to users
    const usersUnsub = onSnapshot(collection(db, 'users'), (snapshot) => {
      const usersData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(usersData);
    });

    // Subscribe to pending guides
    const guidesQuery = query(
      collection(db, 'dev_board_messages'),
      where('isGuide', '==', true),
      where('isValidatedSolution', '==', false)
    );
    const guidesUnsub = onSnapshot(guidesQuery, (snapshot) => {
      const guidesData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setPendingGuides(guidesData);
    });

    // Subscribe to admin logs
    const logsUnsub = onSnapshot(collection(db, 'admin_logs'), (snapshot) => {
      const logsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort by timestamp descending
      logsData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setLogs(logsData);
    });

    // Subscribe to user activity logs
    const userLogsQuery = query(collection(db, 'user_activity_logs'), orderBy('timestamp', 'desc'));
    const userLogsUnsub = onSnapshot(userLogsQuery, (snapshot) => {
      const uLogsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setUserLogs(uLogsData);
    });

    setLoading(false);

    return () => {
      usersUnsub();
      guidesUnsub();
      logsUnsub();
      userLogsUnsub();
    };
  }, [isAdmin]);

  const toggleBlockUser = async (userId, currentStatus, userName) => {
    if (!isAdmin) return;
    try {
      await updateDoc(doc(db, 'users', userId), {
        isBlocked: !currentStatus
      });
      await logAdminAction(
        !currentStatus ? 'BLOCK_USER' : 'UNBLOCK_USER', 
        `Toggled block status to ${!currentStatus} for user ${userName || userId}`
      );
    } catch (error) {
      console.error("Error toggling block status", error);
      alert("Failed to toggle block status");
    }
  };

  const adminValidateGuide = async (messageId, authorId) => {
    if (!isAdmin) return;
    try {
      // 1. Update the message
      await updateDoc(doc(db, 'dev_board_messages', messageId), {
        isValidatedSolution: true,
        isLocked: true 
      });

      // 2. Award Points
      const targetUser = users.find(u => u.uid === authorId || u.id === authorId);
      if (targetUser) {
        await updateDoc(doc(db, 'users', authorId), {
          devItCreds: (targetUser.devItCreds || 0) + 10,
          lastCredsUpdate: new Date().toISOString()
        });
      }

      await logAdminAction('VALIDATE_GUIDE', `Validated guide ${messageId} and awarded 10 points to author ${authorId}`);
    } catch (error) {
      console.error("Error validating guide as admin", error);
      alert("Failed to validate guide");
    }
  };

  return {
    users,
    pendingGuides,
    logs,
    loading,
    isAdmin,
    toggleBlockUser,
    adminValidateGuide,
    userLogs
  };
}
